import { Picture } from 'Models/Picture/@types';

/** Image extensions used for fallback detection when fileType is not set */
export const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp']);

/**
 * MIME types and extensions for the file input accept attribute.
 * Restricts to common safe types: images, documents, PDFs, spreadsheets,
 * presentations, text/csv, audio, video, and archives.
 */
export const ACCEPTED_FILE_TYPES = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.mp3,.wav,.m4a,video/*';

/**
 * Extracts the file extension from a filename (lowercase, without dot).
 */
export function getFileExtension(filename: string): string {
    if (!filename || typeof filename !== 'string') return '';
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1) return '';
    return filename.slice(lastDot + 1).toLowerCase();
}

/**
 * Returns a display-friendly filename from a Picture (name or derived from url).
 */
export function getFileName(picture: Picture): string {
    if (picture.name) return picture.name;
    const path = picture.url || picture.imagePath;
    if (!path) return 'file';
    const segments = path.split('/');
    const last = segments[segments.length - 1];
    const queryIndex = last.indexOf('?');
    return queryIndex === -1 ? last : last.slice(0, queryIndex);
}

/**
 * Determines if a Picture represents an image for display purposes.
 * Uses fileType if available, otherwise falls back to extension check on name or url.
 */
export function isImageFile(file: Picture): boolean {
    if (file.fileType === 'image') return true;
    if (file.fileType === 'file' || file.fileType === 'non-image') return false;
    const name = getFileName(file);
    const ext = getFileExtension(name);
    return IMAGE_EXTENSIONS.has(ext);
}

/**
 * Determines if a Picture represents a PDF for display purposes.
 * Uses fileType if available, otherwise falls back to extension check on name or url.
 */
export function isPdfFile(file: Picture): boolean {
    if (file.fileType === 'file' || file.fileType === 'non-image') return true;
    const name = getFileName(file);
    const ext = getFileExtension(name);
    return ext === 'pdf';
}
